import { Row, Col, Card, Divider } from "antd";
import ChartTitle from "components/ChartTitle";
import { GRID_GUTTER } from "constants/ui";
import React, { useMemo } from "react";
import ReportSection from "../ReportSection";
import FTUOverview from "../../../FTUTooltipsWizard/components/FTUOverview";
import TableChart from "components/charts/TableChart";
import { useFormatNumber } from "hooks/useFormatNumber";
import {
  DATAKEY_USER_ACTIVATION,
  DATAKEY_MERKEN_IMPRESSIONS,
  DATAKEY_MERKEN_CLICKS_BY_VARIANT,
  DATAKEY_PLUGIT_DOCK,
  DATAKEY_ONBOARDING_TOOLTIP_WIZARD,
  DATAKEY_TOOLTIP_OPEN,
  DATAKEY_TOTAL_ACTIVATION,
  DATAKEY_CONVERSION_RATE,
  DATAKEY_USER_SIGNUP,
  DATAKEY_SIGNUP_RATE,
} from "../../constants";
import ConversionRateSection from "../ConversionRateSection";
import WizardDropoffSection from "../WizardDropoffSection";

const UserEngagementAnalyticsOverview = ({
  userActivationData,
  conversionRateData,
  merkenImpressionsVariantIds,
  merkenClicksByVariantVariantIds,
  merkenTableData,
  ftuData,
  wizardStepData,
  wizardImpressions,
  wizardDismissals,
  wizardRegisterClicks,
  wizardDismissRate,
}) => {
  const formatNumber = useFormatNumber();
  // chart lines for user activation: Plugit Dock + First Time Tooltip (Tooltip + Wizard)
  const activationChartLines = useMemo(
    () => [
      {
        name: "Plugit dock",
        dataKey: DATAKEY_PLUGIT_DOCK,
      },
      {
        name: "Onboarding tooltip & wizard",
        dataKey: DATAKEY_ONBOARDING_TOOLTIP_WIZARD,
      },
    ],
    []
  );

  const activationCategoryScorecards = useMemo(
    () => [
      { dataKey: DATAKEY_PLUGIT_DOCK, title: "Plugit dock" },
      {
        dataKey: DATAKEY_ONBOARDING_TOOLTIP_WIZARD,
        title: "Onboarding tooltip & wizard",
      },
    ],
    []
  );

  // chart lines for merken impressions (dynamic variant IDs)
  const merkenImpressionsChartLines = useMemo(
    () =>
      (merkenImpressionsVariantIds || []).map((variantId) => ({
        name: `Variant ${variantId}`,
        dataKey: variantId,
      })),
    [merkenImpressionsVariantIds]
  );

  // chart lines for merken clicks by variant (dynamic variant IDs)
  const merkenClicksByVariantChartLines = useMemo(
    () =>
      (merkenClicksByVariantVariantIds || []).map((variantId) => ({
        name: `Variant ${variantId}`,
        dataKey: variantId,
      })),
    [merkenClicksByVariantVariantIds]
  );

  return (
    <>
      <Row gutter={GRID_GUTTER}>
        <ReportSection
          data={userActivationData}
          totalDataKey={DATAKEY_USER_ACTIVATION}
          title="Total user activation"
          chartLines={activationChartLines}
          categoryScorecards={activationCategoryScorecards}
        />
      </Row>

      <Row gutter={GRID_GUTTER}>
        <ConversionRateSection
          title="Conversion rate"
          data={conversionRateData}
          openMetricLabel="Tooltip Views (Unique Users)"
          openDataKey={DATAKEY_TOOLTIP_OPEN}
          signupLabel="Tooltip-Driven Signups"
          signupDataKey={DATAKEY_USER_SIGNUP}
          signupRateDataKey={DATAKEY_SIGNUP_RATE}
          activationLabel="Tooltip-Driven Activations"
          activationRateDataKey={DATAKEY_CONVERSION_RATE}
          activationDataKey={DATAKEY_TOTAL_ACTIVATION}
        />
      </Row>

      <Row gutter={GRID_GUTTER}>
        <FTUOverview data={ftuData} />
      </Row>

      <Row gutter={GRID_GUTTER}>
        <WizardDropoffSection
          wizardStepData={wizardStepData}
          wizardImpressions={wizardImpressions}
          wizardDismissals={wizardDismissals}
          wizardRegisterClicks={wizardRegisterClicks}
          wizardDismissRate={wizardDismissRate}
        />
      </Row>

      {/* <Row gutter={GRID_GUTTER}>
        <ReportSection
          data={merkenImpressionsData}
          totalDataKey={DATAKEY_MERKEN_IMPRESSIONS}
          title="Merken button impressions by variant"
          chartLines={merkenImpressionsChartLines}
          variantIds={merkenImpressionsVariantIds}
        />

        <ReportSection
          data={merkenClicksByVariantData}
          totalDataKey={DATAKEY_MERKEN_CLICKS_BY_VARIANT}
          title="Merken button clicks by variant"
          chartLines={merkenClicksByVariantChartLines}
          variantIds={merkenClicksByVariantVariantIds}
        />

        <ReportSection
          data={merkenCTRByVariantData}
          totalDataKey={DATAKEY_MERKEN_CTR}
          title="Merken Click-through Rate (CTR) by Variant"
          chartLines={merkenCTRByVariantChartLines}
          variantIds={merkenCTRByVariantVariantIds}
          variantDataKeyFormatter={(variantId) => `${variantId}_CTR`}
        />
      </Row> */}

      {merkenTableData && merkenTableData.length > 0 && (
        <Row gutter={GRID_GUTTER} className="mt-8">
          <Col span={24}>
            <ChartTitle>Merken Button Report</ChartTitle>
          </Col>
          <Col xs={24} className="mb-8">
            <Card size="small">
              <TableChart
                rowKey="variantId"
                dataSource={merkenTableData}
                pagination={false}
                columns={[
                  {
                    title: "Variant ID",
                    dataIndex: "variantId",
                    key: "variantId",
                  },
                  {
                    title: "Zone Type",
                    dataIndex: "zoneType",
                    key: "zoneType",
                  },
                  {
                    title: "Platform",
                    dataIndex: "platform",
                    key: "platform",
                  },
                  {
                    title: "Impressions",
                    dataIndex: "impressions",
                    key: "impressions",
                    align: "right",
                    render: (value) => formatNumber(value),
                  },
                  {
                    title: "Clicks",
                    dataIndex: "clicks",
                    key: "clicks",
                    align: "right",
                    render: (value) => formatNumber(value),
                  },
                  {
                    title: "CTR (Click-Through Rate)",
                    dataIndex: "ctr",
                    key: "ctr",
                    align: "right",
                    render: (value) =>
                      value != null ? `${value.toFixed(1)}%` : "-",
                  },
                ]}
              />
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default UserEngagementAnalyticsOverview;
